'use client';

import { Box, Chip, IconButton, Stack } from '@mui/material';
import { percentFormatter } from '@web/utils/currency';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import NextImage from 'next/image';
import { FlexBox, FlexRowCenter } from '@web/components/flex-box';
import { useState } from 'react';
import useProductCollection from '@web/useProductionCollection';

import { notFound } from 'next/navigation';
import OptimizedImage from '@web/components/OptimizedImage';

export default function ImageSelector({
  product,
}: {
  product: {
    title: string;
    image: string;
    discount: number;
    thumbnails: [];
  };
}) {
  if (!product.title) {
    notFound();
  }

  const [selectedImage, setSelectedImage] = useState(product.image);
  const handleImageClick = (image) => () => setSelectedImage(image);
  const { isProductAdded, handleCollectionClick } =
    useProductCollection(product);

  return (
    <Box pb={2}>
      <FlexBox justifyContent="center" mb={1} sx={{ position: 'relative' }}>
        {product.discount > 0.01 && (
          <Chip
            color="primary"
            label={`${percentFormatter.format(product.discount)} off`}
            sx={{
              zIndex: 1,
              top: '10px',
              left: '10px',
              my: 1,
              paddingLeft: 2,
              paddingRight: 2,
              fontWeight: 600,
              fontSize: '13px',
              position: 'absolute',
            }}
          />
        )}
        <IconButton
          onClick={handleCollectionClick}
          sx={{
            zIndex: 1,
            top: '2px',
            right: '1px',
            position: 'absolute',
          }}
        >
          {isProductAdded ? (
            <FavoriteIcon color="primary" sx={{ fontSize: 40 }} />
          ) : (
            <FavoriteBorderIcon sx={{ fontSize: 40 }} />
          )}
        </IconButton>
        {selectedImage && (
          <OptimizedImage
            alt={product.title}
            width={300}
            height={300}
            src={selectedImage}
            style={{
              width: { xs: '60%', md: '100%' } as any,
              height: 'auto',
              objectFit: 'contain',
              aspectRatio: '1',
            }}
          />
        )}
      </FlexBox>
      {product.thumbnails?.length > 1 && (
        <Stack direction="row" overflow="auto">
          {product.thumbnails?.map((url, ind) => (
            <FlexRowCenter
              key={ind}
              width={64}
              height={64}
              minWidth={64}
              bgcolor="white"
              border="1px solid"
              borderRadius="10px"
              ml={ind === 0 ? 'auto' : 0}
              style={{
                cursor: 'pointer',
              }}
              onClick={handleImageClick(url)}
              mr={ind === product.thumbnails?.length - 1 ? 'auto' : '10px'}
              borderColor={selectedImage === url ? 'primary.main' : 'grey.400'}
            >
              {url && product.title && (
                <OptimizedImage
                  src={url}
                  alt={product.title}
                  width={56}
                  height={56}
                  style={{
                    objectFit: 'contain',
                  }}
                />
              )}
            </FlexRowCenter>
          ))}
        </Stack>
      )}
    </Box>
  );
}
